import { ApolloError } from "@apollo/client/errors";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactsIcon from "@mui/icons-material/Contacts";
import DeleteIcon from "@mui/icons-material/Delete";
import FactoryIcon from "@mui/icons-material/Factory";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HelpIcon from "@mui/icons-material/Help";
import MapIcon from "@mui/icons-material/Map";
import SettingsIcon from "@mui/icons-material/Settings";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { FeatureFlagKey, type FeatureFlagMap } from "encamp-shared/src/featureFlags";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useProducts } from "hooks/useProducts";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

import { NavSection, type NavItem } from "../../components/LeftNavV2/LeftNavDrawerV2";
import { type GroupChildNavItem } from "../../components/LeftNavV2/NavItemV2";
import ScoutIcon from "../../components/ScoutIcon";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useStaffNavSectionsV2 } from "../Staff/staffNavSectionsV2";

export type CustomerNavBuilderOptions = {
  flags: Partial<FeatureFlagMap> | undefined;
  hasPermissions: (permissions: Permission[]) => boolean;
  isPartner: boolean;
  isStaff: boolean;
  staffNavSections: NavSection[];
  tenantId: string | undefined;
  hasProducts: boolean;
  productCatalogLabel: string;
};

function createGroupChildNavItem(text: string, path: string): GroupChildNavItem {
  return {
    kind: "GroupChild",
    text,
    path,
  };
}

export function buildCustomerNavSectionsV2({
  flags,
  hasPermissions,
  isPartner,
  isStaff,
  staffNavSections,
  tenantId,
  hasProducts,
  productCatalogLabel,
}: CustomerNavBuilderOptions): NavSection[] {
  if (!tenantId) {
    if (isStaff) {
      return staffNavSections;
    }

    return [];
  }

  const navItems: NavSection[] = [];
  const isChemicalsEnabled = Boolean(flags?.[FeatureFlagKey.TierIIModule]);
  const isWasteEnabled = Boolean(flags?.[FeatureFlagKey.WasteModule]);

  const chemicalsChildren: GroupChildNavItem[] = [
    createGroupChildNavItem("Overview", `o/${tenantId}/chemicals/overview`),
    ...(hasPermissions([Permission.RouteEpcraReports])
      ? [createGroupChildNavItem("Reports", `o/${tenantId}/chemicals/reports`)]
      : []),
    createGroupChildNavItem("Inventory", `o/${tenantId}/chemicals/inventory`),
    ...(hasPermissions([Permission.RouteEpcraProducts])
      ? [createGroupChildNavItem("Chem Catalog", `o/${tenantId}/chemicals/catalog/chemicals`)]
      : []),
    ...(hasProducts && hasPermissions([Permission.RouteEpcraProducts])
      ? [createGroupChildNavItem(productCatalogLabel, `o/${tenantId}/chemicals/products`)]
      : []),
    ...(hasPermissions([Permission.RouteContacts])
      ? [createGroupChildNavItem("Chem Contacts", `o/${tenantId}/chemicals/contacts`)]
      : []),
    ...(hasPermissions([Permission.RouteDocuments])
      ? [createGroupChildNavItem("Chem Documents", `o/${tenantId}/chemicals/documents`)]
      : []),
  ];

  const wasteChildren: GroupChildNavItem[] = [
    createGroupChildNavItem("Overview", `o/${tenantId}/waste/overview`),
    createGroupChildNavItem("Waste Facilities", `o/${tenantId}/waste/facilities`),
    createGroupChildNavItem("Manifests", `o/${tenantId}/waste/manifests`),
    createGroupChildNavItem("Line Items", `o/${tenantId}/waste/manifest-line-items`),
    ...(flags?.[FeatureFlagKey.WasteSubmissions]
      ? [createGroupChildNavItem("Submissions", `o/${tenantId}/waste/submissions`)]
      : []),
  ];

  const programItems: NavItem[] = [
    {
      kind: "Group",
      text: "Chemicals",
      description:
        "Access Tier II reporting tools, inventories, chemical catalog, and program contacts/documents",
      Icon: WarehouseIcon,
      path: isChemicalsEnabled ? `o/${tenantId}/chemicals/overview` : `o/${tenantId}/chemicals`,
      isExpandable: isChemicalsEnabled,
      children: isChemicalsEnabled ? chemicalsChildren : [],
    },
  ];

  programItems.push({
    kind: "Group",
    text: "Waste",
    description:
      "See your waste totals, individual facility details, track generator status and manifest information, and manage waste submissions",
    Icon: DeleteIcon,
    path: isWasteEnabled ? `o/${tenantId}/waste/overview` : `o/${tenantId}/waste`,
    isExpandable: isWasteEnabled,
    children: isWasteEnabled ? wasteChildren : [],
  });

  if (programItems.length > 0) {
    navItems.push({ title: "Programs", longTitle: "Program areas", items: programItems });
  }

  const toolkitItems: NavItem[] = [
    ...(flags?.[FeatureFlagKey.Scout]
      ? [
          {
            text: "Scout",
            description: "Explore Scout tools and recommendations for your organization",
            kind: "Standard" as const,
            Icon: ScoutIcon,
            path: `o/${tenantId}/scout`,
          },
        ]
      : []),
    ...(flags?.[FeatureFlagKey.TasksModule]
      ? [
          {
            text: "Tasks",
            description:
              "Keep track of your compliance calendar with deadline tracking and custom reminders",
            kind: "Standard" as const,
            Icon: AssignmentTurnedInIcon,
            path: `o/${tenantId}/tasks`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteInsights])
      ? [
          {
            text: "Insights",
            description:
              "Understand what regulators see across your facilities in terms of compliance and benchmark your organization against peers",
            kind: "Standard" as const,
            Icon: MapIcon,
            path: `o/${tenantId}/insights`,
          },
        ]
      : []),
  ];

  if (toolkitItems.length > 0) {
    navItems.push({ title: "Toolkit", longTitle: "Producivity Toolkit", items: toolkitItems });
  }

  const catalogItems: NavItem[] = [
    ...(hasPermissions([Permission.RouteFacilities])
      ? [
          {
            text: "Facilities",
            description:
              "See information about your facilities and easily access program-specific information",
            kind: "Standard" as const,
            Icon: FactoryIcon,
            path: `o/${tenantId}/facilities`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteContacts])
      ? [
          {
            text: "Contacts",
            description: "View and manage details about your contacts across programs",
            kind: "Standard" as const,
            Icon: ContactsIcon,
            path: `o/${tenantId}/contacts`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteDocuments])
      ? [
          {
            text: "Documents",
            description: "Access documents across your facilities to support your compliance",
            kind: "Standard" as const,
            Icon: ArticleIcon,
            path: `o/${tenantId}/documents`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteEpcraCredentials])
      ? [
          {
            text: "Credentials",
            description: "View, edit, and manage your credentials to various portals and systems",
            kind: "Standard" as const,
            Icon: BadgeIcon,
            path: `o/${tenantId}/credentials`,
          },
        ]
      : []),
    ...(isPartner
      ? [
          {
            text: "Organizations",
            description: "Manage and view your partner organizations.",
            kind: "Standard" as const,
            Icon: GroupsRoundedIcon,
            path: `o/${tenantId}/organizations`,
          },
        ]
      : []),
  ];

  if (catalogItems.length > 0) {
    navItems.push({ title: "Catalogs", items: catalogItems });
  }

  const setupItems: NavItem[] = [
    ...(hasPermissions([Permission.WriteAllUserTenant])
      ? [
          {
            kind: "Standard" as const,
            text: "Account",
            Icon: SettingsIcon,
            path: `o/${tenantId}/account/users`,
            isDisabled: false,
          },
        ]
      : []),
    {
      kind: "Standard",
      text: "Help",
      Icon: HelpIcon,
      path: "http://support.encamp.com",
      isDisabled: false,
      isExternal: true,
    },
  ];

  if (setupItems.length > 0) {
    navItems.push({ title: "Setup", items: setupItems });
  }

  if (isStaff) {
    navItems.push(...staffNavSections);
  }

  return navItems;
}

export function useCustomerNavSectionsV2(): {
  error: ApolloError | undefined;
  navItems: NavSection[];
} {
  const { isStaff, isPartner } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  const { error, tenant, isLKQ } = useTenant();
  const { hasProducts } = useProducts();
  const { featureFlags: flags } = useFeatureFlags();
  const staffNavSections = useStaffNavSectionsV2();

  const navItems = useMemo<NavSection[]>(() => {
    return buildCustomerNavSectionsV2({
      flags,
      hasPermissions,
      isPartner,
      isStaff,
      staffNavSections,
      tenantId: tenant?.id,
      hasProducts: Boolean(hasProducts),
      productCatalogLabel: isLKQ ? "Products / Equipment Catalog" : "Product Catalog",
    });
  }, [flags, hasPermissions, hasProducts, isLKQ, isPartner, isStaff, staffNavSections, tenant?.id]);

  return { error, navItems };
}
